import type { FallbackRender } from '@sentry/react';

type FallbackProps = Parameters<FallbackRender>[0];


/**
 * ErrorFallback is shown by the Sentry ErrorBoundary when a React render
 * error is caught. It gives the user a way to recover without a full reload.
 */
export function ErrorFallback({ error, resetError }: FallbackProps) {
    return (
        <div style={{
            minHeight: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'var(--surface)',
            padding: '2rem',
        }}>
            <div style={{
                maxWidth: 480,
                textAlign: 'center',
                padding: '2.5rem',
                background: 'var(--surface-raised)',
                borderRadius: 16,
                border: '1px solid rgba(var(--danger-rgb), 0.3)',
                boxShadow: '0 0 40px rgba(var(--danger-rgb), 0.08)',
            }}>
                <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>⚠️</div>
                <h2 style={{ color: 'var(--danger)', fontWeight: 800, fontSize: '1.25rem', marginBottom: '0.5rem' }}>
                    Something went wrong
                </h2>
                <p style={{ color: 'var(--ink-muted)', fontSize: '0.875rem', marginBottom: '1.5rem', lineHeight: 1.6 }}>
                    An unexpected error occurred. It has been automatically reported and we'll look into it.
                </p>
                {import.meta.env.DEV && (
                    <pre style={{
                        background: 'var(--track)',
                        border: '1px solid var(--border)',
                        borderRadius: 8,
                        padding: '0.75rem',
                        fontSize: '0.75rem',
                        color: 'var(--danger)',
                        textAlign: 'left',
                        overflow: 'auto',
                        maxHeight: 160,
                        marginBottom: '1.5rem',
                    }}>
                    {(error as Error | null)?.message}
                    </pre>
                )}
                <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'center' }}>
                    <button
                        onClick={resetError}
                        style={{
                            padding: '0.6rem 1.25rem',
                            background: 'var(--accent)',
                            color: 'var(--on-accent)',
                            border: 'none',
                            borderRadius: 8,
                            fontWeight: 700,
                            cursor: 'pointer',
                            fontSize: '0.875rem',
                        }}
                    >
                        Try again
                    </button>
                    <button
                        onClick={() => window.location.href = '/'}
                        style={{
                            padding: '0.6rem 1.25rem',
                            background: 'var(--surface)',
                            color: 'var(--ink-muted)',
                            border: '1px solid var(--border)',
                            borderRadius: 8,
                            fontWeight: 600,
                            cursor: 'pointer',
                            fontSize: '0.875rem',
                        }}
                    >
                        Go home
                    </button>
                </div>
            </div>
        </div>
    );
}
